import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  parseISO,
  isSameDay,
} from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { useLanguage } from "../context/LanguageContext";
import { Skeleton } from "src/components/ui/skeleton";

export default function TimeSelector({ loading, data }) {
  const { language } = useLanguage();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("day");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Sample course data
  const coursesData = [
    {
      course_name: { en: "Course 1", ar: "كورس 1" },
      teacher_name: "Teacher A",
      date: "2025-04-24",
      start_time: "10:15:00",
      end_time: "13:30:00", // 3-hour course
    },
    {
      course_name: { en: "Course 2", ar: "كورس 2" },
      teacher_name: "Teacher B",
      date: "2025-04-15",
      start_time: "14:00:00",
      end_time: "16:00:00", // 2-hour course
    },
    {
      course_name: { en: "Course 3", ar: "كورس 3" },
      teacher_name: "Teacher C",
      date: "2025-04-16",
      start_time: "09:00:00",
      end_time: "12:00:00", // 3-hour course
    },
    {
      course_name: { en: "Course 4", ar: "كورس 4" },
      teacher_name: "Teacher D",
      date: "2025-04-17",
      start_time: "11:00:00",
      end_time: "14:00:00", // 3-hour course
    },
    {
      course_name: { en: "Course 5", ar: "كورس 5" },
      teacher_name: "Teacher E",
      date: "2025-04-18",
      start_time: "14:00:00",
      end_time: "17:00:00", // 3-hour course
    },
  ];

  // Full day time slots from 1 AM to 12 AM (midnight) in 24-hour format
  const fullDayTimeSlots = [
    "01:00",
    "02:00",
    "03:00",
    "04:00",
    "05:00",
    "06:00",
    "07:00",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
    "23:00",
    "00:00",
  ];

  // Get days of current week
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
  const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Check if a time slot has a course
  const getCourseForTimeSlot = (date, timeSlot) => {
    const [hours] = timeSlot.split(":");
    const hourNum = Number.parseInt(hours, 10);

    return data.find((course) => {
      if (!isSameDay(parseISO(course.date), date)) return false;

      const courseStartHour = Number.parseInt(
        course.start_time.split(":")[0],
        10
      );
      // bump the end-time by one hour:
      const courseEndHour = Number.parseInt(course.end_time.split(":")[0], 10);

      // now include everything from start … up through end+1
      return hourNum >= courseStartHour && hourNum <= courseEndHour;
    });
  };

  // Function to get availability for a specific day
  const getAvailabilityForDay = (date) => {
    return fullDayTimeSlots.map((time) => {
      const course = getCourseForTimeSlot(date, time);
      return {
        time,
        selected: !!course,
        course: course,
      };
    });
  };

  // Week availability
  const weekAvailability = useMemo(() => {
    const weekData = {};

    daysOfWeek.forEach((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      weekData[dayStr] = getAvailabilityForDay(day);
    });

    return weekData;
  }, [daysOfWeek, data]);

  // Sample time slots for a single day
  const dayTimeSlots = useMemo(() => {
    return getAvailabilityForDay(selectedDate);
  }, [selectedDate, data]);

  const handlePrevious = () => {
    if (view === "day") {
      setSelectedDate(subDays(selectedDate, 1));
    } else {
      setSelectedDate(subDays(selectedDate, 7));
    }
  };

  const handleNext = () => {
    if (view === "day") {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setSelectedDate(addDays(selectedDate, 7));
    }
  };

  const handleTimeSlotClick = (slot) => {
    if (slot.course) {
      setSelectedCourse(slot.course);
      setDialogOpen(true);
    }
  };

  // Format "HH:mm": drop ":00" if minutes are zero
  const formatHM = (t) => {
    const hm = t.slice(0, 5);
    return hm;
  };

  // Skeleton loaders
  const DayViewSkeleton = () => (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {Array(12)
        .fill(0)
        .map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-md" />
        ))}
    </div>
  );

  const WeekViewSkeleton = () => (
    <div className="overflow-x-auto">
      <div className="min-w-full">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {Array(7)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array(7)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="space-y-1">
                {Array(8)
                  .fill(0)
                  .map((_, j) => (
                    <Skeleton key={j} className="h-8 w-full rounded-md" />
                  ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full text-primary overflow-auto">
      <div className="flex justify-between items-center mb-2 p-3 bg-primary dark:bg-gray-100 text-background rounded-t-xl">
        <div className="inline-flex bg-background rounded-xl overflow-hidden">
          {["week", "day"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 text-sm ${
                view === v ? "bg-blue-600 text-white" : "text-primary"
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={handlePrevious}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm">
            {view === "day"
              ? format(selectedDate, "EEEE, MMMM d, yyyy")
              : `${format(weekStart, "MMM d")} - ${format(
                  weekEnd,
                  "MMM d, yyyy"
                )}`}
          </span>
          <button onClick={handleNext}>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {loading ? (
        view === "day" ? (
          <DayViewSkeleton />
        ) : (
          <WeekViewSkeleton />
        )
      ) : (
        <>
          {/* --- Day View --- */}
          {view === "day" && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {(() => {
                const groups = [];
                let curr = null,
                  buf = [];

                dayTimeSlots.forEach((slot) => {
                  if (slot.course) {
                    if (
                      !curr ||
                      curr.course_name.en !== slot.course.course_name.en
                    ) {
                      if (buf.length) {
                        groups.push({ course: curr, slots: buf });
                      }
                      buf = [];
                      curr = slot.course;
                    }
                    buf.push(slot);
                  } else {
                    if (buf.length) {
                      groups.push({ course: curr, slots: buf });
                      buf = [];
                      curr = null;
                    }
                    groups.push({ course: null, slots: [slot] });
                  }
                });
                if (buf.length) groups.push({ course: curr, slots: buf });

                return groups.map((g, i) =>
                  g.course ? (
                    <div
                      key={i}
                      className={`col-span-${g.slots.length} p-3 rounded-md bg-blue-600 text-white cursor-pointer flex flex-col justify-center items-center`}
                      style={{ gridColumn: `span ${g.slots.length}` }}
                      onClick={() => handleTimeSlotClick(g.slots[0])}
                    >
                      <div className="text-sm font-medium">
                        {formatHM(g.course.start_time)} -{" "}
                        {formatHM(g.course.end_time)}
                      </div>
                      <div className="text-xs mt-1">
                        {g.course.course_name[language] ||
                          g.course.course_name.en}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="p-3 rounded-md bg-gray-100 text-black flex items-center justify-center"
                    >
                      <span className="text-sm font-medium">
                        {g.slots[0].time}
                      </span>
                    </div>
                  )
                );
              })()}
            </div>
          )}

          {/* --- Week View --- */}
          {view === "week" && (
            <div className="overflow-x-auto">
              <div className="min-w-full">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {daysOfWeek.map((d) => (
                    <div
                      key={format(d, "yyyy-MM-dd")}
                      className="text-center p-2 whitespace-nowrap"
                    >
                      <div className="font-medium">{format(d, "EEE")}</div>
                      <div className="text-sm text-gray-400">
                        {format(d, "MMM d")}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Slots grid */}
                <div className="grid grid-cols-7 gap-1">
                  {daysOfWeek.map((d) => {
                    const dayStr = format(d, "yyyy-MM-dd");
                    const slots = weekAvailability[dayStr] || [];

                    // group same as day view
                    const groups = [];
                    let curr = null,
                      buf = [];
                    slots.forEach((slot) => {
                      if (slot.course) {
                        if (
                          !curr ||
                          curr.course_name.en !== slot.course.course_name.en
                        ) {
                          if (buf.length)
                            groups.push({ course: curr, slots: buf });
                          buf = [];
                          curr = slot.course;
                        }
                        buf.push(slot);
                      } else {
                        if (buf.length) {
                          groups.push({ course: curr, slots: buf });
                          buf = [];
                          curr = null;
                        }
                        groups.push({ course: null, slots: [slot] });
                      }
                    });
                    if (buf.length) groups.push({ course: curr, slots: buf });

                    return (
                      <div key={dayStr} className="space-y-1">
                        {groups.map((g, idx) =>
                          g.course ? (
                            <div
                              key={idx}
                              className="p-2 rounded-md bg-blue-600 text-white cursor-pointer flex flex-col justify-center items-center"
                              style={{ height: `${g.slots.length * 33 - 1}px` }}
                              onClick={() => handleTimeSlotClick(g.slots[0])}
                            >
                              <div className="text-xs font-medium">
                                {formatHM(g.course.start_time)} -{" "}
                                {formatHM(g.course.end_time)}
                              </div>
                              <div className="text-xs">
                                {g.course.course_name[language] ||
                                  g.course.course_name.en}
                              </div>
                            </div>
                          ) : (
                            <div
                              key={idx}
                              className="p-1 rounded-md bg-gray-100 text-black flex items-center justify-center"
                            >
                              <span className="text-xs font-medium">
                                {g.slots[0].time}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* --- Course Dialog --- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-md"
          dir={language === "en" ? "ltr" : "rtl"}
        >
          <DialogHeader>
            <DialogTitle>Course Details</DialogTitle>
          </DialogHeader>
          {selectedCourse && (
            <div className="p-4" dir={language === "en" ? "ltr" : "rtl"}>
              <div className="mb-2">
                <span className="font-bold">Course: </span>
                <span>
                  {selectedCourse.course_name.en} (
                  {selectedCourse.course_name.ar})
                </span>
              </div>
              <div className="mb-2">
                <span className="font-bold">Teacher: </span>
                <span>{selectedCourse.teacher_name}</span>
              </div>
              <div className="mb-2">
                <span className="font-bold">Date: </span>
                <span>
                  {format(parseISO(selectedCourse.date), "MMMM d, yyyy")}
                </span>
              </div>
              <div>
                <span className="font-bold">Time: </span>
                <span>
                  {formatHM(selectedCourse.start_time)} -{" "}
                  {formatHM(selectedCourse.end_time)}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
